import { createSelector } from '@reduxjs/toolkit';

import { getIsHotelOnly } from 'src/store/view/selectors';
import { flightsCountSelector } from '../../CheckoutRoutes/PackageCheckout/store/flights/selectors';
import { getIsOrderPrebuildFetching } from './order';
import { getIsManualPackage } from './package';

import type { ActualizationStateType } from '../actualization/types';
import { PRICE_CHANGE_DIRECTION } from '../constants/actualization';

const getActualizationData = (state): ActualizationStateType =>
  state.actualization;

export const getActualizationFetching = createSelector(
  getActualizationData,
  (actualizationState) => actualizationState.fetching,
);

export const getActualizationRotten = createSelector(
  getActualizationData,
  (actualizationState) => actualizationState.rotten,
);

export const getActualizationSuccess = createSelector(
  getActualizationData,
  (actualizationState) => actualizationState.success,
);

export const getActualizationShowModalCustomer = createSelector(
  getActualizationData,
  (actualizationState) => actualizationState.show_modal_customer,
);

/**
 * Может использоваться как проверка на то, отработала ли первая актуализация,
 * так как до нее равно null
 */
export const getLastUpdated = createSelector(
  getActualizationData,
  (actualizationState) => actualizationState.lastUpdated,
);

export const getHotelFacts = createSelector(
  getActualizationData,
  (actualizationState) => actualizationState.hotelFacts,
);

export const getShowPriceChanged = createSelector(
  getActualizationData,
  (actualizationState) => actualizationState.showPriceChanged,
);
export const getIsActualizationProcess = createSelector(
  getActualizationData,
  (actualizationState) => actualizationState.isActualizationProcess,
);
export const getIsActualizationOrdered = createSelector(
  getActualizationData,
  (actualizationState) => actualizationState.ordered,
);

export const getPrebookData = createSelector(
  getActualizationData,
  (actualization) => actualization.prebook_data || {},
);

export const getIsSubmitButtonLoading = (state) =>
  getActualizationFetching(state) || getIsOrderPrebuildFetching(state);

export const actualizationExceptionSelector = createSelector(
  getActualizationData,
  (actualizationState) => actualizationState.exception,
);

export const frontErrorSelector = createSelector(
  getActualizationData,
  (actualizationState) => actualizationState.frontError,
);

export const actualizationFetchingWithoutFlightsSelector = (state) => {
  const isHotelOnly = getIsHotelOnly(state);
  const isManualPackage = getIsManualPackage(state);

  if (isHotelOnly || isManualPackage) return getActualizationFetching(state);

  return getActualizationFetching(state) || flightsCountSelector(state) === 0;
};

export const selectCurrentPrice = createSelector(
  getActualizationData,
  (actualization): number | null => actualization.currentPrice,
);

export const selectPreviousPrice = createSelector(
  getActualizationData,
  (actualization): number | null => actualization.previousPrice,
);

export const selectIsPriceChanged = createSelector(
  [selectPreviousPrice, selectCurrentPrice],
  (previous, current) => {
    if (previous === current || !previous || !current) return false;

    return Boolean(current - previous);
  },
);

export const selectPriceChangeDirection = createSelector(
  [selectPreviousPrice, selectCurrentPrice],
  (previous, current) => {
    if (previous === current || !previous || !current) return undefined;

    return previous < current
      ? PRICE_CHANGE_DIRECTION.UP
      : PRICE_CHANGE_DIRECTION.DOWN;
  },
);

/**
 * Возвращает цену пакета, которую клиент видел на странице отеля
 */
export const selectPriceFromHotelPage = (state) =>
  getActualizationData(state).changes.full_price?.[0];
